/**
 * Bulk Events Creation Logic
 * 
 * Handles sequential creation of events with throttling and error handling.
 * Continues on errors and tracks progress.
 * 
 * Requirements: 6.5-6.9
 */

import { restClient, CreateEventDto } from '../api/rest-client';
import type { BatchProgress } from '../components/features/bulkEvents/BatchCreationProgress';
import type { BatchResults } from '../components/features/bulkEvents/ResultsSummary';

/**
 * Sleep for specified milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Format date for display
 */
function formatEventDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Create events sequentially with throttling
 * 
 * Creates events one at a time with a 1-second delay between each.
 * Continues on errors and tracks successful/failed creations.
 * 
 * @param events - Array of events to create
 * @param onProgress - Callback for progress updates
 * @returns Promise resolving to batch results
 * 
 * Requirements: 6.5-6.9
 */
export async function createEventsSequentially(
  events: CreateEventDto[],
  onProgress: (progress: BatchProgress) => void
): Promise<BatchResults> {
  const results: BatchResults = {
    successful: [],
    failed: [],
  };
  
  for (let i = 0; i < events.length; i++) {
    const event = events[i];
    
    // Update progress before attempting creation
    onProgress({
      current: i,
      total: events.length,
      currentEventName: event.event_name,
      currentEventDate: formatEventDate(event.event_starttime),
    });
    
    try {
      // Attempt to create the event
      await restClient.createEvent(event);
      
      // Track successful creation
      results.successful.push({
        eventName: event.event_name,
        eventDate: formatEventDate(event.event_starttime),
        success: true,
      });
    } catch (error) {
      // Track failed creation but continue with remaining events
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      results.failed.push({
        eventName: event.event_name,
        eventDate: formatEventDate(event.event_starttime),
        success: false,
        error: errorMessage,
      });
      
      console.error(`[BulkEvents] Failed to create event "${event.event_name}":`, error);
    }
    
    // Throttle: Wait 1 second before next API call (except after last event)
    if (i < events.length - 1) {
      await sleep(1000);
    }
  }
  
  // Final progress update
  onProgress({
    current: events.length,
    total: events.length,
  });
  
  return results;
}

export default createEventsSequentially;
